import type { SurfaceMaterial } from "../types";
import type { GroundPoint } from "./dugArea";

export type ScatterKind = "tufts" | "dryTufts" | "flowers" | "stones" | "leaves";

export const SCATTER_CHUNK_METERS = 50;
const MAX_ELEMENTS_PER_KIND = 60000;

interface ScatterLayer {
  kind: ScatterKind;
  perSquareMeter: number;
  /** Échelle tirée entre ces bornes. */
  scale?: [number, number];
  /** Couleurs propres à cette matière, à la place de celles du type d'élément. */
  colors?: string[];
  /** Ne sème que par plaques : là où le bruit des taches dépasse ce seuil. */
  patches?: number;
}

const LAYERS_BY_MATERIAL: Partial<Record<SurfaceMaterial, ScatterLayer[]>> = {
  grass: [{ kind: "tufts", perSquareMeter: 2 }, { kind: "flowers", perSquareMeter: 0.06 }],
  dirt: [
    { kind: "stones", perSquareMeter: 0.7, scale: [0.3, 0.75], colors: ["#6f5843", "#a38c72"] },
    { kind: "stones", perSquareMeter: 0.06, scale: [1, 2.3], colors: ["#7b6a5a", "#a69683"] },
    { kind: "dryTufts", perSquareMeter: 2.4, scale: [0.45, 0.8], patches: 0.56 },
  ],
  sand: [
    { kind: "stones", perSquareMeter: 0.18, scale: [0.2, 0.55], colors: ["#cbbd9f", "#f1e8d4"] },
    { kind: "dryTufts", perSquareMeter: 1.4, scale: [0.4, 0.7], colors: ["#b3a265", "#ddce95"], patches: 0.63 },
  ],
  gravel: [
    { kind: "stones", perSquareMeter: 0.8, scale: [0.3, 0.7], colors: ["#8d8a83", "#b9b5ac"] },
    { kind: "stones", perSquareMeter: 0.05, scale: [1, 1.8], colors: ["#837f78", "#aaa59c"] },
    { kind: "tufts", perSquareMeter: 1.3, scale: [0.35, 0.6], colors: ["#647a40", "#87975a"], patches: 0.66 },
  ],
  chips: [
    { kind: "leaves", perSquareMeter: 0.45 },
    { kind: "leaves", perSquareMeter: 0.35, scale: [1.3, 2.4], colors: ["#5a3d26", "#8a6440"] },
    { kind: "stones", perSquareMeter: 0.05, scale: [0.5, 1.2], colors: ["#6f5843", "#9a8268"] },
  ],
};

const LAYERS_BY_PRESET: Record<ScatterPreset, ScatterLayer[]> = {
  pebbleBorder: [
    { kind: "stones", perSquareMeter: 30, scale: [0.55, 1.5], colors: ["#8b867c", "#d6cfc0"] },
    { kind: "stones", perSquareMeter: 4, scale: [1.5, 2.2], colors: ["#9a958a", "#cbc4b4"] },
  ],
};

export const SCATTER_COLORS: Record<ScatterKind, string[]> = {
  tufts: ["#6a9f3f", "#8dbb55"],
  dryTufts: ["#8c7a3c", "#c2ad62"],
  flowers: ["#f2e15c", "#f4f1ec", "#e98bb2", "#f2b33d"],
  stones: ["#8e8b84", "#a6a39b", "#7c776f", "#b3aea5"],
  leaves: ["#c9782e", "#e0a33b", "#9a4f22", "#b5652a"],
};

/** Un semis qui ne dépend pas d'une matière : celui d'une bordure. */
export type ScatterPreset = "pebbleBorder";

export interface ScatterArea {
  id: string;
  material: SurfaceMaterial;
  preset?: ScatterPreset;
  /** Ne sème qu'à moins de cette distance du contour, en mètres, et plus serré contre lui. */
  band?: number;
  ring: GroundPoint[];
  holes: GroundPoint[][];
  base: number;
  /** Multiplie le nombre d'éléments semés. */
  density: number;
  /** Teinte de la forme (`rrggbb`), que l'habillage reprend. */
  tint?: string;
  /** Emprise de la forme en longitude/latitude, marge comprise, quand les routes qui passent dessus doivent rester dégagées. */
  roadBounds?: [number, number, number, number];
  /** Voies qui passent au-dessus de la forme : rien n'y est semé. Déduites de `roadBounds` au moment du semis. */
  clearings?: { lines: { path: GroundPoint[]; half: number }[]; areas: GroundPoint[][] };
}

/** Les éléments d'une forme qui tombent dans un carreau, triés par `ranks` croissants. */
export interface ScatterContribution {
  chunk: string;
  kind: ScatterKind;
  matrices: Float32Array;
  colors: Float32Array;
  ranks: Float32Array;
}

export function scatterKindsOf(material: SurfaceMaterial): ScatterKind[] {
  return [...new Set((LAYERS_BY_MATERIAL[material] ?? []).map(({ kind }) => kind))];
}

export function seeded(text: string): () => number {
  let state = 2166136261;
  for (let i = 0; i < text.length; i++) state = Math.imul(state ^ text.charCodeAt(i), 16777619);
  state >>>= 0;
  return () => {
    state ^= state << 13;
    state ^= state >>> 17;
    state ^= state << 5;
    return ((state >>> 0) % 1000003) / 1000003;
  };
}

function inside([x, z]: GroundPoint, ring: GroundPoint[]): boolean {
  let hit = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, zi] = ring[i]!;
    const [xj, zj] = ring[j]!;
    if (zi > z !== zj > z && x < ((xj - xi) * (z - zi)) / (zj - zi) + xi) hit = !hit;
  }
  return hit;
}

function area(ring: GroundPoint[]): number {
  let sum = 0;
  for (let i = 0; i < ring.length; i++) {
    const [x1, z1] = ring[i]!;
    const [x2, z2] = ring[(i + 1) % ring.length]!;
    sum += x1 * z2 - x2 * z1;
  }
  return Math.abs(sum / 2);
}

/** Points tirés dans la forme, toujours les mêmes pour une même forme, dans un ordre aléatoire. */
export function sampleScatter(target: Pick<ScatterArea, "id" | "ring" | "holes">, kind: ScatterKind, perSquareMeter: number): GroundPoint[] {
  const rand = seeded(`${target.id}:${kind}`);
  const xs = target.ring.map(([x]) => x);
  const zs = target.ring.map(([, z]) => z);
  const [minX, maxX, minZ, maxZ] = [Math.min(...xs), Math.max(...xs), Math.min(...zs), Math.max(...zs)];
  const surface = area(target.ring) - target.holes.reduce((sum, hole) => sum + area(hole), 0);
  const count = Math.min(MAX_ELEMENTS_PER_KIND, Math.round(surface * perSquareMeter));
  const points: GroundPoint[] = [];
  for (let attempt = 0; points.length < count && attempt < count * 8; attempt++) {
    const point: GroundPoint = [minX + rand() * (maxX - minX), minZ + rand() * (maxZ - minZ)];
    if (inside(point, target.ring) && !target.holes.some((hole) => inside(point, hole))) points.push(point);
  }
  return points;
}

function linear(channel: number): number {
  return channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
}

function rgb(hex: string): [number, number, number] {
  const value = parseInt(hex.replace("#", ""), 16);
  return [((value >> 16) & 255) / 255, ((value >> 8) & 255) / 255, (value & 255) / 255];
}

/** Bruit lisse entre 0 et 1 : la teinte de l'herbe varie par taches de quelques mètres. */
function patchNoise(x: number, z: number): number {
  const hash = (i: number, j: number) => {
    const n = Math.sin(i * 127.1 + j * 311.7) * 43758.5453;
    return n - Math.floor(n);
  };
  const smooth = (t: number) => t * t * (3 - 2 * t);
  const sample = (u: number, v: number) => {
    const i = Math.floor(u);
    const j = Math.floor(v);
    const fu = smooth(u - i);
    const fv = smooth(v - j);
    const top = hash(i, j) + (hash(i + 1, j) - hash(i, j)) * fu;
    const bottom = hash(i, j + 1) + (hash(i + 1, j + 1) - hash(i, j + 1)) * fu;
    return top + (bottom - top) * fv;
  };
  return sample(x / 7, z / 7) * 0.65 + sample(x / 2.3 + 17, z / 2.3 + 5) * 0.35;
}

function tintedPalette(tint: string): [number, number, number][] {
  const [r, g, b] = rgb(tint);
  return [0.72, 0.85, 1, 1.12].map((factor) => [Math.min(1, r * factor), Math.min(1, g * factor), Math.min(1, b * factor)]);
}

/** Points tirés le long du contour, vers l'intérieur de la forme, plus serrés contre lui. */
export function sampleBand(target: Pick<ScatterArea, "id" | "ring" | "holes">, kind: ScatterKind, perSquareMeter: number, band: number): GroundPoint[] {
  const rand = seeded(`${target.id}:${kind}:band`);
  const within = (point: GroundPoint) => inside(point, target.ring) && !target.holes.some((hole) => inside(point, hole));
  const points: GroundPoint[] = [];
  for (const ring of [target.ring, ...target.holes]) {
    for (let index = 0; index < ring.length; index++) {
      const [ax, az] = ring[index]!;
      const [bx, bz] = ring[(index + 1) % ring.length]!;
      const length = Math.hypot(bx - ax, bz - az);
      if (length < 0.01) continue;
      let [nx, nz] = [-(bz - az) / length, (bx - ax) / length];
      if (!within([(ax + bx) / 2 + nx * 0.05, (az + bz) / 2 + nz * 0.05])) [nx, nz] = [-nx, -nz];
      const count = Math.min(MAX_ELEMENTS_PER_KIND, Math.round(length * band * perSquareMeter));
      for (let k = 0; k < count; k++) {
        const t = rand();
        const depth = band * rand() ** 1.4;
        const point: GroundPoint = [ax + (bx - ax) * t + nx * depth, az + (bz - az) * t + nz * depth];
        if (within(point)) points.push(point);
      }
    }
  }
  return points;
}

const CLEARING_CELL_M = 10;

/** Vrai pour un point posé sur une voie ; les segments sont rangés par cases pour n'en tester que quelques-uns. */
export function clearingTest(clearings: ScatterArea["clearings"]): (point: GroundPoint) => boolean {
  if (!clearings || (!clearings.lines.length && !clearings.areas.length)) return () => false;
  const cells = new Map<string, { a: GroundPoint; b: GroundPoint; half: number }[]>();
  for (const { path, half } of clearings.lines) {
    for (let index = 1; index < path.length; index++) {
      const segment = { a: path[index - 1]!, b: path[index]!, half };
      const [x0, x1] = [Math.min(segment.a[0], segment.b[0]) - half, Math.max(segment.a[0], segment.b[0]) + half];
      const [z0, z1] = [Math.min(segment.a[1], segment.b[1]) - half, Math.max(segment.a[1], segment.b[1]) + half];
      for (let cx = Math.floor(x0 / CLEARING_CELL_M); cx <= Math.floor(x1 / CLEARING_CELL_M); cx++) {
        for (let cz = Math.floor(z0 / CLEARING_CELL_M); cz <= Math.floor(z1 / CLEARING_CELL_M); cz++) {
          const key = `${cx}:${cz}`;
          const list = cells.get(key);
          if (list) list.push(segment);
          else cells.set(key, [segment]);
        }
      }
    }
  }
  const areaCells = new Map<string, { ring: GroundPoint[]; bounds: [number, number, number, number] }[]>();
  for (const ring of clearings.areas) {
    const xs = ring.map(([x]) => x);
    const zs = ring.map(([, z]) => z);
    const area = { ring, bounds: [Math.min(...xs), Math.min(...zs), Math.max(...xs), Math.max(...zs)] as [number, number, number, number] };
    for (let cx = Math.floor(area.bounds[0] / CLEARING_CELL_M); cx <= Math.floor(area.bounds[2] / CLEARING_CELL_M); cx++) {
      for (let cz = Math.floor(area.bounds[1] / CLEARING_CELL_M); cz <= Math.floor(area.bounds[3] / CLEARING_CELL_M); cz++) {
        const key = `${cx}:${cz}`;
        const list = areaCells.get(key);
        if (list) list.push(area);
        else areaCells.set(key, [area]);
      }
    }
  }
  return (point) => {
    const cell = `${Math.floor(point[0] / CLEARING_CELL_M)}:${Math.floor(point[1] / CLEARING_CELL_M)}`;
    const near = cells.get(cell) ?? [];
    for (const { a, b, half } of near) {
      const dx = b[0] - a[0];
      const dz = b[1] - a[1];
      const length2 = dx * dx + dz * dz;
      const t = length2 ? Math.max(0, Math.min(1, ((point[0] - a[0]) * dx + (point[1] - a[1]) * dz) / length2)) : 0;
      if (Math.hypot(point[0] - a[0] - dx * t, point[1] - a[1] - dz * t) < half) return true;
    }
    return (areaCells.get(cell) ?? []).some(({ ring, bounds: [x0, z0, x1, z1] }) => point[0] >= x0 && point[0] <= x1 && point[1] >= z0 && point[1] <= z1 && inside(point, ring));
  };
}

/** Tout ce que la page doit recevoir pour dessiner l'habillage d'une forme : de quoi remplir les lots directement. */
export function computeScatterArea(target: ScatterArea, openings: GroundPoint[][]): ScatterContribution[] {
  const buckets = new Map<string, { kind: ScatterKind; points: { point: GroundPoint; layer: ScatterLayer }[] }>();
  const onRoad = clearingTest(target.clearings);
  ((target.preset ? LAYERS_BY_PRESET[target.preset] : LAYERS_BY_MATERIAL[target.material]) ?? []).forEach((layer, index) => {
    const keep = seeded(`${target.id}:patches:${index}`);
    const sowing = { ...target, id: `${target.id}:${index}` };
    const sown = target.band ? sampleBand(sowing, layer.kind, layer.perSquareMeter * target.density, target.band) : sampleScatter(sowing, layer.kind, layer.perSquareMeter * target.density);
    for (const point of sown) {
      if (target.base === 0 && openings.some((opening) => inside(point, opening))) continue;
      if (onRoad(point)) continue;
      if (layer.patches !== undefined && patchNoise(point[0] + 37, point[1] - 11) + (keep() - 0.5) * 0.12 < layer.patches) continue;
      const chunk = `${target.tint ? "near:" : ""}${layer.kind}:${Math.floor(point[0] / SCATTER_CHUNK_METERS)}:${Math.floor(point[1] / SCATTER_CHUNK_METERS)}`;
      let bucket = buckets.get(chunk);
      if (!bucket) buckets.set(chunk, (bucket = { kind: layer.kind, points: [] }));
      bucket.points.push({ point, layer });
    }
  });
  const contributions: ScatterContribution[] = [];
  for (const [chunk, { kind, points }] of buckets) {
    const rand = seeded(`${target.id}:${chunk}`);
    const palettes = new Map<ScatterLayer, [number, number, number][]>();
    const paletteOf = (layer: ScatterLayer) => {
      let palette = palettes.get(layer);
      if (!palette) palettes.set(layer, (palette = target.tint && kind !== "flowers" ? tintedPalette(target.tint) : (layer.colors ?? SCATTER_COLORS[kind]).map(rgb)));
      return palette;
    };
    const colorAt = ([x, z]: GroundPoint, layer: ScatterLayer): [number, number, number] => {
      const palette = paletteOf(layer);
      if (kind === "flowers") return palette[Math.floor(rand() * palette.length)]!;
      const [dark, light] = [palette[0]!, palette[palette.length - 1]!];
      const grass = kind === "tufts" || kind === "dryTufts";
      const mix = Math.max(0, Math.min(1, patchNoise(x, z) + (rand() - 0.5) * (grass ? 0.15 : 0.5)));
      return [0, 1, 2].map((channel) => dark[channel]! + (light[channel]! - dark[channel]!) * mix) as [number, number, number];
    };
    const order = points.map(({ point, layer }) => {
      const [low, high] = layer.scale ?? [0.7, 1.3];
      return { point, rank: rand(), angle: rand() * Math.PI * 2, scale: low + rand() * (high - low), color: colorAt(point, layer) };
    });
    order.sort((a, b) => a.rank - b.rank);
    const matrices = new Float32Array(order.length * 16);
    const colors = new Float32Array(order.length * 3);
    const ranks = new Float32Array(order.length);
    order.forEach(({ point: [x, z], rank, angle, scale, color }, index) => {
      const cos = Math.cos(angle) * scale;
      const sin = Math.sin(angle) * scale;
      matrices.set([cos, 0, -sin, 0, 0, scale, 0, 0, sin, 0, cos, 0, x, target.base, z, 1], index * 16);
      colors.set(color.map(linear), index * 3);
      ranks[index] = rank;
    });
    contributions.push({ chunk, kind, matrices, colors, ranks });
  }
  return contributions;
}
